import React, { useState, useEffect, useRef } from "react";
import { db } from "../firebase";
import { collection, getDocs, onSnapshot } from "firebase/firestore";
import { FiChevronLeft, FiX, FiUsers, FiCompass, FiFileText, FiBook } from "react-icons/fi";
import toast from "react-hot-toast";

const LORE_TYPES = {
    character: { label: "Character", color: "#d97706", icon: FiUsers },
    lore: { label: "Lore Element", color: "#0d9488", icon: FiCompass },
    note: { label: "General Note", color: "#6366f1", icon: FiFileText }
};

export default function Canvas() {
    const [sagas, setSagas] = useState([]);
    const [loadingSagas, setLoadingSagas] = useState(true);
    const [selectedSaga, setSelectedSaga] = useState(null);
    
    const [loreNodes, setLoreNodes] = useState([]);
    const [loadingLore, setLoadingLore] = useState(false);
    
    const [selectedNode, setSelectedNode] = useState(null); // Node details to show in sidebar drawer
    const [highlightedNodeId, setHighlightedNodeId] = useState(null); // Temporary pulse highlight
    const [lightboxImage, setLightboxImage] = useState(null);
    const [lightboxZoom, setLightboxZoom] = useState(1);
    
    // Whiteboard Canvas Annotations States
    const [decorations, setDecorations] = useState([]);
    
    // Spoiler tab settings
    const [spoilerTab, setSpoilerTab] = useState("standard"); // "standard", "book1", "book2"

    // Toggle state to show/hide items marked as spoilers
    const [showSpoilers, setShowSpoilers] = useState(false);

    useEffect(() => {
        setSpoilerTab("standard");
    }, [selectedNode]);

    useEffect(() => {
        setLightboxZoom(1);
    }, [lightboxImage]);

    const boardRef = useRef(null);
    const canvasOuterRef = useRef(null);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [panState, setPanState] = useState({ isPanning: false, startX: 0, startY: 0, panX: 0, panY: 0 });
    const [canvasZoom, setCanvasZoom] = useState(0.75); // 75% zoom works well on 1600x1200 workspace

    // Load Sagas
    useEffect(() => {
        const unsub = onSnapshot(collection(db, "sagas"), (snap) => {
            const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            setSagas(list);
            setLoadingSagas(false);
        }, () => {
            toast.error("Failed to load lore universe.");
            setLoadingSagas(false);
        });
        return () => unsub();
    }, []);

    // Load Lore nodes when saga selected
    useEffect(() => {
        if (!selectedSaga) {
            setLoreNodes([]);
            return;
        }
        setLoadingLore(true);
        setSelectedNode(null);
        setShowSpoilers(false);
        
        const unsub = onSnapshot(collection(db, "sagas", selectedSaga.id, "lore"), (snap) => {
            const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            setLoreNodes(list);
            setLoadingLore(false);
            
            // Center on the content bounding box at default zoom
            setTimeout(() => {
                const list2 = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                if (!boardRef.current || list2.length === 0) return;
                const WORKSPACE_W = 1600, WORKSPACE_H = 1200;
                const xs = list2.map(n => (n.x / 100) * WORKSPACE_W);
                const ys = list2.map(n => (n.y / 100) * WORKSPACE_H);
                const midX = (Math.min(...xs) + Math.max(...xs)) / 2;
                const midY = (Math.min(...ys) + Math.max(...ys)) / 2;
                const initialZoom = 0.75;
                setPan({
                    x: boardRef.current.clientWidth / 2 - midX * initialZoom,
                    y: boardRef.current.clientHeight / 2 - midY * initialZoom
                });
            }, 250);
        }, () => {
            toast.error("Failed to load saga lore board.");
            setLoadingLore(false);
        });
        return () => unsub();
    }, [selectedSaga]);

    // Load Decorations
    useEffect(() => {
        if (!selectedSaga) {
            setDecorations([]);
            return;
        }
        const unsub = onSnapshot(collection(db, "sagas", selectedSaga.id, "decorations"), (snap) => {
            const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            setDecorations(list);
        }, (err) => {
            console.error("Failed to load decorations:", err);
        });
        return () => unsub();
    }, [selectedSaga]);

    // Drag-to-pan handlers
    const handleMouseDown = (e) => {
        // Do not trigger pan if clicking on a node, button, or details drawer
        if (e.target.closest(".glass-node") || e.target.closest("button") || e.target.closest(".detail-drawer")) return;
        setPanState({
            isPanning: true,
            startX: e.clientX,
            startY: e.clientY,
            panX: pan.x,
            panY: pan.y
        });
    };

    useEffect(() => {
        if (!panState.isPanning) return;

        const handleMouseMoveGlobal = (e) => {
            const dx = e.clientX - panState.startX;
            const dy = e.clientY - panState.startY;
            setPan({
                x: panState.panX + dx,
                y: panState.panY + dy
            });
        };

        const handleMouseUpGlobal = () => {
            setPanState(prev => ({ ...prev, isPanning: false }));
        };

        window.addEventListener("mousemove", handleMouseMoveGlobal);
        window.addEventListener("mouseup", handleMouseUpGlobal);
        return () => {
            window.removeEventListener("mousemove", handleMouseMoveGlobal);
            window.removeEventListener("mouseup", handleMouseUpGlobal);
        };
    }, [panState.isPanning, panState.startX, panState.startY, panState.panX, panState.panY]);

    // Fit all nodes into the visible canvas viewport
    const fitToNodes = (nodes) => {
        const nodeList = nodes || loreNodes;
        if (!boardRef.current || nodeList.length === 0) return;
        const containerW = boardRef.current.clientWidth;
        const containerH = boardRef.current.clientHeight;
        const WORKSPACE_W = 1600;
        const WORKSPACE_H = 1200;
        const xs = nodeList.map(n => (n.x / 100) * WORKSPACE_W);
        const ys = nodeList.map(n => (n.y / 100) * WORKSPACE_H);
        const minX = Math.min(...xs);
        const maxX = Math.max(...xs);
        const minY = Math.min(...ys);
        const maxY = Math.max(...ys);
        const pad = 120;
        const contentW = Math.max(maxX - minX + pad * 2, 300);
        const contentH = Math.max(maxY - minY + pad * 2, 220);
        // Clamp zoom so cards stay readable (never below 0.35, never above 1.5)
        const newZoom = Math.max(0.35, Math.min(containerW / contentW, containerH / contentH, 1.5));
        setCanvasZoom(newZoom);
        const midX = (minX + maxX) / 2;
        const midY = (minY + maxY) / 2;
        setPan({
            x: containerW / 2 - midX * newZoom,
            y: containerH / 2 - midY * newZoom
        });
    };

    const zoomCanvas = (direction) => {
        const delta = direction === "in" ? 0.08 : -0.08;
        setCanvasZoom(prev => {
            const next = Math.min(2.0, Math.max(0.08, parseFloat((prev + delta).toFixed(2))));
            if (boardRef.current) {
                const rect = boardRef.current.getBoundingClientRect();
                const mx = rect.width / 2;
                const my = rect.height / 2;
                setPan(currentPan => {
                    const cx = (mx - currentPan.x) / prev;
                    const cy = (my - currentPan.y) / prev;
                    return {
                        x: mx - cx * next,
                        y: my - cy * next
                    };
                });
            }
            return next;
        });
    };

    // Mouse-wheel zoom toward cursor (non-passive)
    useEffect(() => {
        const el = canvasOuterRef.current;
        if (!el) return;
        const handler = (e) => {
            e.preventDefault();
            const delta = e.deltaY > 0 ? -0.04 : 0.04;
            setCanvasZoom(prev => {
                const next = Math.min(2.0, Math.max(0.08, parseFloat((prev + delta).toFixed(3))));
                // Zoom toward mouse cursor
                if (boardRef.current) {
                    const rect = boardRef.current.getBoundingClientRect();
                    const mx = e.clientX - rect.left;
                    const my = e.clientY - rect.top;
                    setPan(currentPan => {
                        const cx = (mx - currentPan.x) / prev;
                        const cy = (my - currentPan.y) / prev;
                        return {
                            x: mx - cx * next,
                            y: my - cy * next
                        };
                    });
                }
                return next;
            });
        };
        el.addEventListener('wheel', handler, { passive: false });
        return () => el.removeEventListener('wheel', handler);
    }, [selectedSaga]);

    // Jump to connected node and highlight it
    const jumpToNode = (id) => {
        const node = loreNodes.find(n => n.id === id);
        if (node) {
            setSelectedNode(node);
            setHighlightedNodeId(id);
            setTimeout(() => setHighlightedNodeId(null), 2500);
            if (boardRef.current) {
                const nodeX = (node.x / 100) * 1600;
                const nodeY = (node.y / 100) * 1200;
                boardRef.current.scrollTo({
                    left: nodeX - boardRef.current.clientWidth / 2,
                    top: nodeY - boardRef.current.clientHeight / 2,
                    behavior: "smooth"
                });
            }
        }
    };

    // Class styles & CSS variables injected
    const styles = `
        .lore-canvas-page {
            background-color: #0b0f19;
            color: #f3f4f6;
            height: 100vh;
            font-family: 'Inter', sans-serif;
            position: relative;
            overflow: hidden;
            display: flex;
            flex-direction: column;
        }

        .canvas-bg-glow {
            position: absolute;
            width: 600px;
            height: 600px;
            background: radial-gradient(circle, rgba(26, 107, 74, 0.15) 0%, rgba(0,0,0,0) 70%);
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            pointer-events: none;
            z-index: 0;
        }

        /* 3D Book Layout styles */
        .book-wrapper {
            position: relative;
            width: 130px;
            height: 185px;
            perspective: 1000px;
            cursor: pointer;
            transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
        }

        .book-cover-3d {
            width: 100%;
            height: 100%;
            border-radius: 3px 8px 8px 3px;
            position: relative;
            transform-style: preserve-3d;
            transition: transform 0.6s cubic-bezier(0.25, 0.8, 0.25, 1), box-shadow 0.6s ease;
            box-shadow: 4px 6px 15px rgba(0, 0, 0, 0.6), inset 3px 0 5px rgba(255, 255, 255, 0.1);
        }

        .book-spine-effect {
            position: absolute;
            top: 0;
            left: 0;
            width: 10px;
            height: 100%;
            background: rgba(0, 0, 0, 0.25);
            border-radius: 3px 0 0 3px;
            box-shadow: inset -1px 0 2px rgba(255, 255, 255, 0.1);
        }

        .book-pages-effect {
            position: absolute;
            right: 4px;
            top: 4px;
            bottom: 4px;
            width: 6px;
            background: #eee6dc;
            border-radius: 0 3px 3px 0;
            box-shadow: inset 1px 0 2px rgba(0,0,0,0.15);
        }

        .book-wrapper:hover {
            transform: translateY(-10px);
        }

        .book-wrapper:hover .book-cover-3d {
            transform: rotateY(-22deg);
            box-shadow: 18px 20px 30px rgba(0, 0, 0, 0.7), inset 3px 0 5px rgba(255, 255, 255, 0.15);
        }

        /* Skin decorations */
        .bind-gothic {
            border: 2px solid #5a1111;
            box-shadow: inset 0 0 10px rgba(255, 0, 0, 0.15);
        }
        .bind-gothic::after {
            content: "☠";
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            font-size: 24px;
            color: rgba(255,255,255,0.08);
        }

        .bind-golden {
            border: 2px solid #c8a96e;
        }
        .bind-golden::after {
            content: "✦";
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            font-size: 24px;
            color: rgba(200, 169, 110, 0.15);
        }

        .bind-cosmic {
            border: 2px solid #8b5cf6;
        }
        .bind-cosmic::after {
            content: "✵";
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            font-size: 26px;
            color: rgba(139, 92, 246, 0.2);
        }

        .bind-classic {
            border-left: 5px solid rgba(0,0,0,0.35);
        }
        .bind-classic::after {
            content: "📖";
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            font-size: 24px;
            color: rgba(255,255,255,0.05);
        }

        /* Glassmorphic Nodes */
        .glass-node {
            background: rgba(15, 23, 42, 0.7);
            backdrop-filter: blur(12px);
            -webkit-backdrop-filter: blur(12px);
            border-radius: 12px;
            box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.37);
            transition: transform 0.2s ease, box-shadow 0.2s ease;
        }

        .glass-node:hover {
            transform: translate(-50%, -50%) scale(1.05) !important;
            box-shadow: 0 10px 40px 0 rgba(0, 0, 0, 0.5);
            cursor: pointer;
        }

        /* Pulse Animation on Highlight */
        .highlight-pulse {
            animation: pulse-border 1s infinite alternate;
        }

        @keyframes pulse-border {
            from {
                box-shadow: 0 0 10px rgba(255,255,255,0.2), 0 0 20px rgba(255,255,255,0.1);
                transform: translate(-50%, -50%) scale(1.05) !important;
            }
            to {
                box-shadow: 0 0 20px rgba(255, 255, 255, 0.8), 0 0 35px var(--glow-color);
                transform: translate(-50%, -50%) scale(1.15) !important;
            }
        }

        /* Detail Drawer */
        .detail-drawer {
            position: fixed;
            top: 0;
            right: -420px;
            width: 400px;
            height: 100%;
            background: #0f172a;
            border-left: 2px solid #1e293b;
            box-shadow: -10px 0 30px rgba(0,0,0,0.5);
            z-index: 1000;
            transition: right 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
            display: flex;
            flex-direction: column;
        }

        .detail-drawer.open {
            right: 0;
        }

        .drawer-body::-webkit-scrollbar {
            width: 6px;
        }
        .drawer-body::-webkit-scrollbar-thumb {
            background: #334155;
            border-radius: 3px;
        }
    `;

    const activeSaga = selectedSaga ? (sagas.find(s => s.id === selectedSaga.id) || selectedSaga) : null;

    return (
        <div className="lore-canvas-page">
            <style>{styles}</style>
            <div className="canvas-bg-glow" />

            {activeSaga ? (
                /* Inner Lore Map Workspace */
                <div style={{ flex: 1, display: "flex", flexDirection: "column", position: "relative" }}>
                    {/* Header Controls */}
                    <div style={{ 
                        display: "flex", 
                        alignItems: "center", 
                        justifyContent: "space-between", 
                        padding: "16px 24px", 
                        background: "rgba(15, 23, 42, 0.8)", 
                        borderBottom: "1px solid #1e293b",
                        zIndex: 10
                    }}>
                        <button 
                            onClick={() => setSelectedSaga(null)} 
                            style={{ 
                                display: "flex", 
                                alignItems: "center", 
                                gap: 6, 
                                background: "none", 
                                border: "none", 
                                color: "#94a3b8", 
                                cursor: "pointer", 
                                fontSize: 14,
                                fontWeight: 600
                            }}
                        >
                            <FiChevronLeft size={16} /> Back to Sagas
                        </button>
                        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "white" }}>
                            {activeSaga.title}
                        </h2>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: showSpoilers ? "#f43f5e" : "#94a3b8", transition: "color 0.2s", userSelect: "none" }}>
                                Show Spoilers
                            </span>
                            <button
                                onClick={() => setShowSpoilers(!showSpoilers)}
                                style={{
                                    width: 44,
                                    height: 24,
                                    borderRadius: 12,
                                    background: showSpoilers ? "#f43f5e" : "#334155",
                                    border: "none",
                                    position: "relative",
                                    cursor: "pointer",
                                    padding: 0,
                                    transition: "background-color 0.2s ease",
                                    display: "flex",
                                    alignItems: "center"
                                }}
                            >
                                <div style={{
                                    width: 18,
                                    height: 18,
                                    borderRadius: "50%",
                                    background: "white",
                                    position: "absolute",
                                    left: showSpoilers ? 22 : 4,
                                    transition: "left 0.2s ease",
                                    boxShadow: "0 1px 3px rgba(0,0,0,0.3)"
                                }} />
                            </button>
                        </div>
                    </div>

                        <div 
                            ref={canvasOuterRef}
                            style={{ position: "relative", flex: 1, display: "flex", flexDirection: "column" }}
                        >
                         {/* Background Backdrop Layer - Fits Browser Resolution Exactly */}
                        {activeSaga.bgImageUrl && (
                            <div style={{
                                position: "absolute",
                                top: 0, left: 0, right: 0, bottom: 0,
                                backgroundImage: `url("${activeSaga.bgImageUrl}")`,
                                backgroundSize: "cover",
                                backgroundPosition: activeSaga.bgImagePosition || "center center",
                                backgroundRepeat: "no-repeat",
                                zIndex: 0
                            }} />
                        )}
                        {/* Background Color Overlay - Fits Browser Resolution Exactly */}
                        {activeSaga.bgImageUrl && (
                            <div style={{
                                position: "absolute",
                                top: 0, left: 0, right: 0, bottom: 0,
                                background: activeSaga.bgOverlayColor || "#000000",
                                opacity: activeSaga.bgOverlayOpacity ?? 0.6,
                                zIndex: 0,
                                pointerEvents: "none"
                            }} />
                        )}

                        {/* Floating Navigation Controls - Google Maps style */}
                        <div style={{
                            position: "absolute",
                            bottom: 24,
                            right: 24,
                            zIndex: 100,
                            display: "flex",
                            flexDirection: "column",
                            gap: 6,
                            alignItems: "center"
                        }}>
                            {/* Zoom In */}
                            <button
                                onClick={() => zoomCanvas("in")}
                                title="Zoom In"
                                style={{
                                    width: 36, height: 36,
                                    background: "rgba(15, 23, 42, 0.85)",
                                    backdropFilter: "blur(12px)",
                                    border: "1px solid rgba(255,255,255,0.1)",
                                    borderRadius: 8,
                                    color: "white",
                                    fontSize: 20,
                                    fontWeight: 700,
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
                                    transition: "background 0.15s"
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = "rgba(99,102,241,0.7)"}
                                onMouseLeave={e => e.currentTarget.style.background = "rgba(15,23,42,0.85)"}
                            >+</button>

                            {/* Zoom level badge */}
                            <div style={{
                                background: "rgba(15, 23, 42, 0.85)",
                                backdropFilter: "blur(12px)",
                                border: "1px solid rgba(255,255,255,0.1)",
                                borderRadius: 8,
                                color: "#94a3b8",
                                fontSize: 11,
                                fontWeight: 700,
                                padding: "5px 0",
                                width: 36,
                                textAlign: "center",
                                boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
                                cursor: "default"
                            }}>
                                {Math.round(canvasZoom * 100)}%
                            </div>

                            {/* Zoom Out */}
                            <button
                                onClick={() => zoomCanvas("out")}
                                title="Zoom Out"
                                style={{
                                    width: 36, height: 36,
                                    background: "rgba(15, 23, 42, 0.85)",
                                    backdropFilter: "blur(12px)",
                                    border: "1px solid rgba(255,255,255,0.1)",
                                    borderRadius: 8,
                                    color: "white",
                                    fontSize: 20,
                                    fontWeight: 700,
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
                                    transition: "background 0.15s"
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = "rgba(99,102,241,0.7)"}
                                onMouseLeave={e => e.currentTarget.style.background = "rgba(15,23,42,0.85)"}
                            >−</button>

                            {/* Fit to content */}
                            <button
                                onClick={() => fitToNodes()}
                                title="Fit all cards into view"
                                style={{
                                    width: 36, height: 36,
                                    background: "rgba(15, 23, 42, 0.85)",
                                    backdropFilter: "blur(12px)",
                                    border: "1px solid rgba(255,255,255,0.1)",
                                    borderRadius: 8,
                                    color: "#94a3b8",
                                    fontSize: 16,
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
                                    transition: "background 0.15s"
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = "rgba(99,102,241,0.7)"}
                                onMouseLeave={e => e.currentTarget.style.background = "rgba(15,23,42,0.85)"}
                            >⊡</button>
                        </div>

                        <div 
                            ref={boardRef}
                            onMouseDown={handleMouseDown}
                            className="canvas-grid-bg"
                            style={{ 
                                flex: 1, 
                                overflow: "hidden", 
                                cursor: panState.isPanning ? "grabbing" : "grab",
                                position: "relative",
                                background: "transparent",
                                zIndex: 1
                            }}
                        >
                            {/* 1600x1200 workspace */}
                            <div style={{ 
                                width: "1600px", 
                                height: "1200px", 
                                position: "absolute",
                                top: 0,
                                left: 0,
                                pointerEvents: "none",
                                background: "transparent",
                                transform: `translate(${pan.x}px, ${pan.y}px) scale(${canvasZoom})`,
                                transformOrigin: "top left",
                                transition: panState.isPanning ? "none" : "transform 0.12s ease-out"
                            }}>
                                {/* Grid dots pattern layer */}
                                <div style={{
                                    position: "absolute",
                                    top: 0, left: 0, right: 0, bottom: 0,
                                    backgroundImage: "radial-gradient(rgba(255, 255, 255, 0.08) 1.5px, transparent 0)",
                                    backgroundSize: "32px 32px",
                                    zIndex: 0,
                                    pointerEvents: "none"
                                }} />

                            {/* SVG connectors layer */}
                            <svg style={{ position: "absolute", width: "100%", height: "100%", top: 0, left: 0, zIndex: 1, pointerEvents: "none" }}>
                                {loreNodes.map(node => {
                                    if (!showSpoilers && node.isSpoiler) return null;
                                    return (node.connections || []).map(connId => {
                                        const target = loreNodes.find(n => n.id === connId);
                                        if (!target || node.id > target.id) return null; // Avoid duplicate connections
                                        if (!showSpoilers && target.isSpoiler) return null;
                                        return (
                                            <line 
                                                key={`${node.id}-${target.id}`}
                                                x1={`${node.x}%`} 
                                                y1={`${node.y}%`} 
                                                x2={`${target.x}%`} 
                                                y2={`${target.y}%`} 
                                                stroke="rgba(200, 169, 110, 0.25)" 
                                                strokeWidth="2.5" 
                                                style={{ filter: "drop-shadow(0 0 3px rgba(200, 169, 110, 0.15))" }}
                                            />
                                        );
                                    });
                                })}

                                {/* Custom Board Lines */}
                                {decorations.filter(d => d.type === "line").map(d => (
                                    <line 
                                        key={d.id}
                                        x1={`${d.x}%`} 
                                        y1={`${d.y}%`} 
                                        x2={`${d.x2 || (d.x + 10)}%`} 
                                        y2={`${d.y2 || d.y}%`} 
                                        stroke={d.color || "#fbbf24"}
                                        strokeWidth={d.strokeWidth || 3}
                                        strokeDasharray={d.dashed ? "4 4" : "none"}
                                    />
                                ))}
                            </svg>

                            {/* Relative viewBox SVG for Freehand Scribbles */}
                            <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: "absolute", width: "100%", height: "100%", top: 0, left: 0, zIndex: 1, pointerEvents: "none" }}>
                                {decorations.filter(d => d.type === "scribble").map(d => {
                                    if (!d.points || d.points.length < 2) return null;
                                    const pathData = d.points.map((p, idx) => `${idx === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
                                    return (
                                        <path
                                            key={d.id}
                                            d={pathData}
                                            fill="none"
                                            stroke={d.color || "#ff0055"}
                                            strokeWidth={d.strokeWidth ? d.strokeWidth * 0.1 : 0.3}
                                        />
                                    );
                                })}
                            </svg>

                            {/* Whiteboard Decorations Layer */}
                            {decorations.map(d => {
                                if (d.type === "title") {
                                    return (
                                        <div 
                                            key={d.id}
                                            style={{
                                                position: "absolute",
                                                left: `${d.x}%`,
                                                top: `${d.y}%`,
                                                transform: "translate(-50%, -50%)",
                                                color: d.color || "#ffffff",
                                                fontSize: `${d.fontSize || 24}px`,
                                                fontWeight: 700,
                                                whiteSpace: "nowrap",
                                                zIndex: 3,
                                                pointerEvents: "none",
                                                textShadow: "1px 1px 4px rgba(0,0,0,0.8)"
                                            }}
                                        >
                                            {d.text}
                                        </div>
                                    );
                                }
                                if (d.type === "shape") {
                                    return (
                                        <div 
                                            key={d.id}
                                            style={{
                                                position: "absolute",
                                                left: `${d.x}%`,
                                                top: `${d.y}%`,
                                                transform: "translate(-50%, -50%)",
                                                width: `${d.width || 15}%`,
                                                height: `${d.height || 10}%`,
                                                border: `2px solid ${d.color || "#38bdf8"}`,
                                                background: `${d.color || "#38bdf8"}22`,
                                                borderRadius: d.shapeType === "circle" ? "50%" : "8px",
                                                zIndex: 2,
                                                pointerEvents: "none"
                                            }}
                                        />
                                    );
                                }
                                if (d.type === "image") {
                                    return (
                                        <div 
                                            key={d.id}
                                            onClick={(e) => { e.stopPropagation(); if (d.imageUrl) setLightboxImage(d.imageUrl); }}
                                            style={{
                                                position: "absolute",
                                                left: `${d.x}%`,
                                                top: `${d.y}%`,
                                                transform: "translate(-50%, -50%)",
                                                width: `${d.width || 20}%`,
                                                height: `${d.height || 15}%`,
                                                border: "2px solid rgba(255,255,255,0.15)",
                                                borderRadius: "8px",
                                                overflow: "hidden",
                                                zIndex: 2,
                                                background: "#0b0f19",
                                                pointerEvents: "auto",
                                                cursor: d.imageUrl ? "zoom-in" : "default"
                                            }}
                                        >
                                            {d.imageUrl && (
                                                <img 
                                                    src={d.imageUrl} 
                                                    alt="Decoration" 
                                                    style={{ 
                                                        width: "100%", 
                                                        height: "100%", 
                                                        objectFit: "cover", 
                                                        objectPosition: d.imagePosition || "center center" 
                                                    }} 
                                                />
                                            )}
                                        </div>
                                    );
                                }
                                return null;
                            })}

                            {/* Node cards */}
                            {loreNodes.map(n => {
                                if (!showSpoilers && n.isSpoiler) return null;
                                const typeInfo = LORE_TYPES[n.type] || LORE_TYPES.character;
                                const Icon = typeInfo.icon;
                                const isHighlighted = highlightedNodeId === n.id;
                                return (
                                    <div 
                                        key={n.id}
                                        onClick={() => setSelectedNode(n)}
                                        className={`glass-node ${isHighlighted ? "highlight-pulse" : ""}`}
                                        style={{
                                            position: "absolute",
                                            left: `${n.x}%`,
                                            top: `${n.y}%`,
                                            transform: "translate(-50%, -50%)",
                                            border: `2px solid ${typeInfo.color}`,
                                            width: "240px",
                                            height: "260px",
                                            borderRadius: "16px",
                                            overflow: "hidden",
                                            zIndex: 5,
                                            pointerEvents: "auto", // re-enable clicks on the cards
                                            display: "flex",
                                            flexDirection: "column",
                                            "--glow-color": typeInfo.color,
                                            boxShadow: `0 10px 25px rgba(0,0,0,0.5), 0 0 15px ${typeInfo.color}15`
                                        }}
                                    >
                                        {/* Top Image portion */}
                                        <div style={{ position: "relative", width: "100%", height: "160px", flexShrink: 0, background: "#0b0f19" }}>
                                            {n.imageUrl ? (
                                                <img 
                                                    src={n.imageUrl} 
                                                    alt={n.title} 
                                                    style={{ 
                                                        width: "100%", 
                                                        height: "100%", 
                                                        objectFit: "cover", 
                                                        objectPosition: n.imagePosition || "center center" 
                                                    }}
                                                />
                                            ) : (
                                                <div style={{ 
                                                    width: "100%", 
                                                    height: "100%", 
                                                    background: `linear-gradient(135deg, #1e293b 0%, ${typeInfo.color}22 100%)`, 
                                                    display: "flex", 
                                                    alignItems: "center", 
                                                    justifyContent: "center" 
                                                }}>
                                                    <Icon size={44} style={{ color: `${typeInfo.color}66` }} />
                                                </div>
                                            )}
                                            
                                            {/* Bottom Gradient overlay */}
                                            <div style={{
                                                position: "absolute",
                                                bottom: 0,
                                                left: 0,
                                                right: 0,
                                                height: "95px",
                                                background: "linear-gradient(to top, rgba(15, 23, 42, 0.98) 0%, rgba(15, 23, 42, 0.5) 60%, rgba(0,0,0,0) 100%)",
                                                display: "flex",
                                                flexDirection: "column",
                                                justifyContent: "flex-end",
                                                padding: "12px"
                                            }}>
                                                <h4 style={{ 
                                                    margin: 0, 
                                                    fontSize: "17px", 
                                                    fontWeight: 800, 
                                                    color: "white", 
                                                    overflow: "hidden", 
                                                    textOverflow: "ellipsis", 
                                                    whiteSpace: "nowrap",
                                                    fontFamily: "'Playfair Display', Georgia, serif"
                                                }}>
                                                    {n.title}
                                                </h4>
                                                <div>
                                                    <span style={{ 
                                                        display: "inline-block",
                                                        marginTop: "6px",
                                                        fontSize: "9px",
                                                        fontWeight: 700,
                                                        color: typeInfo.color,
                                                        background: `${typeInfo.color}15`,
                                                        border: `1.5px solid ${typeInfo.color}44`,
                                                        padding: "2px 8px",
                                                        borderRadius: "4px",
                                                        textTransform: "uppercase",
                                                        letterSpacing: "0.5px"
                                                    }}>
                                                        {n.type === "character" ? (n.characterRole || "Character") : typeInfo.label}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Bottom Stats portion */}
                                        <div style={{ 
                                            flex: 1, 
                                            background: "rgba(15, 23, 42, 0.95)", 
                                            padding: "12px", 
                                            display: "flex", 
                                            gap: "8px", 
                                            alignItems: "center" 
                                        }}>
                                            {n.type === "character" ? (
                                                <>
                                                    {/* Age Stat Box */}
                                                    <div style={{ 
                                                        flex: 1, 
                                                        minWidth: 0,
                                                        border: "1.5px solid rgba(255,255,255,0.06)", 
                                                        background: "rgba(30, 41, 59, 0.25)", 
                                                        borderRadius: "8px", 
                                                        padding: "6px 8px" 
                                                    }}>
                                                        <span style={{ display: "block", fontSize: "8px", color: "#64748b", fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: "2px" }}>Age</span>
                                                        <span style={{ display: "block", fontSize: "11px", color: "#cbd5e1", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                            {n.age || "—"}
                                                        </span>
                                                    </div>
                                                    
                                                    {/* Class Stat Box */}
                                                    <div style={{ 
                                                        flex: 1, 
                                                        minWidth: 0,
                                                        border: "1.5px solid rgba(255,255,255,0.06)", 
                                                        background: "rgba(30, 41, 59, 0.25)", 
                                                        borderRadius: "8px", 
                                                        padding: "6px 8px" 
                                                    }}>
                                                        <span style={{ display: "block", fontSize: "8px", color: "#64748b", fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: "2px" }}>Class</span>
                                                        <span style={{ display: "block", fontSize: "11px", color: "#cbd5e1", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                            {n.characterClass || "Human"}
                                                        </span>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    {/* Generic Type Box */}
                                                    <div style={{ 
                                                        flex: 1, 
                                                        minWidth: 0,
                                                        border: "1.5px solid rgba(255,255,255,0.06)", 
                                                        background: "rgba(30, 41, 59, 0.25)", 
                                                        borderRadius: "8px", 
                                                        padding: "6px 8px" 
                                                    }}>
                                                        <span style={{ display: "block", fontSize: "8px", color: "#64748b", fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: "2px" }}>Type</span>
                                                        <span style={{ display: "block", fontSize: "11px", color: "#cbd5e1", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                            {typeInfo.label}
                                                        </span>
                                                    </div>

                                                    {/* Connections Box */}
                                                    <div style={{ 
                                                        flex: 1, 
                                                        minWidth: 0,
                                                        border: "1.5px solid rgba(255,255,255,0.06)", 
                                                        background: "rgba(30, 41, 59, 0.25)", 
                                                        borderRadius: "8px", 
                                                        padding: "6px 8px" 
                                                    }}>
                                                        <span style={{ display: "block", fontSize: "8px", color: "#64748b", fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: "2px" }}>Links</span>
                                                        <span style={{ display: "block", fontSize: "11px", color: "#cbd5e1", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                            {n.connections?.length || 0}
                                                        </span>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div> {/* boardRef closing */}
                    </div> {/* canvasOuterRef closing */}

                    {/* Map Legend Overlay */}
                    <div style={{
                        position: "absolute",
                        bottom: "24px",
                        left: "24px",
                        background: "rgba(15, 23, 42, 0.85)",
                        backdropFilter: "blur(12px)",
                        border: "1px solid rgba(255, 255, 255, 0.08)",
                        borderRadius: "12px",
                        padding: "12px 16px",
                        zIndex: 10,
                        display: "flex",
                        gap: "16px",
                        alignItems: "center",
                        fontSize: "11px",
                        boxShadow: "0 10px 25px rgba(0,0,0,0.5)",
                        pointerEvents: "auto"
                    }}>
                        <span style={{ fontWeight: 700, color: "#64748b", textTransform: "uppercase", fontSize: "9px", letterSpacing: "0.5px" }}>Legend</span>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#d97706", boxShadow: "0 0 6px #d97706" }} />
                            <span style={{ color: "#e2e8f0", fontWeight: 500 }}>Character</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#0d9488", boxShadow: "0 0 6px #0d9488" }} />
                            <span style={{ color: "#e2e8f0", fontWeight: 500 }}>Lore Element</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#6366f1", boxShadow: "0 0 6px #6366f1" }} />
                            <span style={{ color: "#e2e8f0", fontWeight: 500 }}>General Note</span>
                        </div>
                    </div>

                    {/* Sliding Details Drawer */}
                    <div className={`detail-drawer ${selectedNode ? "open" : ""}`}>
                        {selectedNode && (
                            <>
                                <div style={{ 
                                    display: "flex", 
                                    alignItems: "center", 
                                    justifyContent: "space-between", 
                                    padding: "20px", 
                                    borderBottom: "1px solid #1e293b",
                                    background: "#0b0f19"
                                }}>
                                    <span style={{ 
                                        fontSize: 10, 
                                        fontWeight: 700, 
                                        textTransform: "uppercase", 
                                        letterSpacing: 0.5,
                                        padding: "3px 8px",
                                        borderRadius: 20,
                                        background: `${(LORE_TYPES[selectedNode.type] || LORE_TYPES.character).color}15`,
                                        color: (LORE_TYPES[selectedNode.type] || LORE_TYPES.character).color,
                                        border: `1px solid ${(LORE_TYPES[selectedNode.type] || LORE_TYPES.character).color}33`
                                    }}>
                                        {(LORE_TYPES[selectedNode.type] || LORE_TYPES.character).label}
                                    </span>
                                    <button 
                                        onClick={() => setSelectedNode(null)}
                                        style={{ background: "none", border: "none", color: "#64748b", cursor: "pointer" }}
                                    >
                                        <FiX size={20} />
                                    </button>
                                </div>
                                <div className="drawer-body" style={{ flex: 1, overflowY: "auto", padding: "20px" }}>
                                    <h3 style={{ margin: "0 0 16px", fontSize: 22, fontWeight: 800, color: "white" }}>
                                        {selectedNode.title}
                                    </h3>
                                    
                                    {selectedNode.imageUrl && (
                                        <img 
                                            src={selectedNode.imageUrl} 
                                            alt={selectedNode.title}
                                            onClick={() => setLightboxImage(selectedNode.imageUrl)}
                                            style={{ 
                                                width: "100%", 
                                                height: "200px", 
                                                objectFit: "cover", 
                                                objectPosition: selectedNode.imagePosition || "center center", 
                                                borderRadius: 12, 
                                                marginBottom: 20, 
                                                border: "1px solid #1e293b",
                                                cursor: "zoom-in"
                                            }}
                                        />
                                    )}

                                    {selectedNode.type === "character" && (
                                        <div style={{
                                            background: "rgba(30, 41, 59, 0.4)",
                                            border: "1px solid rgba(255, 255, 255, 0.08)",
                                            borderRadius: "12px",
                                            padding: "16px",
                                            marginBottom: "20px",
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "12px",
                                            fontSize: "13px"
                                        }}>
                                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px" }}>
                                                <div>
                                                    <span style={{ display: "block", color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", fontSize: "9px", letterSpacing: "0.5px", marginBottom: "2px" }}>Age</span>
                                                    <span style={{ color: "#f1f5f9", fontWeight: 500 }}>{selectedNode.age || "Unknown"}</span>
                                                </div>
                                                <div>
                                                    <span style={{ display: "block", color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", fontSize: "9px", letterSpacing: "0.5px", marginBottom: "2px" }}>Role</span>
                                                    <span style={{ color: "#f1f5f9", fontWeight: 500 }}>{selectedNode.characterRole || selectedNode.characterType || "Protagonist"}</span>
                                                </div>
                                                <div>
                                                    <span style={{ display: "block", color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", fontSize: "9px", letterSpacing: "0.5px", marginBottom: "2px" }}>Class</span>
                                                    <span style={{ color: "#f1f5f9", fontWeight: 500 }}>{selectedNode.characterClass || "Human"}</span>
                                                </div>
                                            </div>
                                            {(selectedNode.physical || selectedNode.personality) && (
                                                <div style={{ borderTop: "1px solid rgba(255, 255, 255, 0.06)", paddingTop: "10px", display: "flex", flexDirection: "column", gap: "10px" }}>
                                                    {selectedNode.physical && (
                                                        <div>
                                                            <span style={{ display: "block", color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", fontSize: "10px", letterSpacing: "0.5px", marginBottom: "2px" }}>Physical Appearance</span>
                                                            <span style={{ color: "#cbd5e1", lineHeight: 1.5 }}>{selectedNode.physical}</span>
                                                        </div>
                                                    )}
                                                    {selectedNode.personality && (
                                                        <div>
                                                            <span style={{ display: "block", color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", fontSize: "10px", letterSpacing: "0.5px", marginBottom: "2px" }}>Personality Traits</span>
                                                            <span style={{ color: "#cbd5e1", lineHeight: 1.5 }}>{selectedNode.personality}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Spoiler Tab Selector */}
                                    <div style={{ 
                                        display: "flex", 
                                        gap: "6px", 
                                        marginBottom: "20px",
                                        background: "rgba(30, 41, 59, 0.4)",
                                        padding: "4px",
                                        borderRadius: "8px",
                                        border: "1px solid rgba(255, 255, 255, 0.05)"
                                    }}>
                                        <button 
                                            onClick={() => setSpoilerTab("standard")}
                                            style={{
                                                flex: 1,
                                                background: spoilerTab === "standard" ? "#1e293b" : "none",
                                                border: "none",
                                                color: spoilerTab === "standard" ? "white" : "#94a3b8",
                                                padding: "6px 8px",
                                                borderRadius: "6px",
                                                fontSize: "11px",
                                                fontWeight: 700,
                                                cursor: "pointer",
                                                transition: "all 0.15s"
                                            }}
                                        >
                                            Preview
                                        </button>
                                        {(showSpoilers || !selectedNode.book1IsSpoiler) && (
                                            <button 
                                                onClick={() => setSpoilerTab("book1")}
                                                style={{
                                                    flex: 1,
                                                    background: spoilerTab === "book1" ? "#b91c1c" : "none",
                                                    border: "none",
                                                    color: spoilerTab === "book1" ? "white" : "#94a3b8",
                                                    padding: "6px 8px",
                                                    borderRadius: "6px",
                                                    fontSize: "11px",
                                                    fontWeight: 700,
                                                    cursor: "pointer",
                                                    transition: "all 0.15s"
                                                }}
                                            >
                                                Did you read book 1? (Spoiler)
                                            </button>
                                        )}
                                        {(showSpoilers || !selectedNode.book2IsSpoiler) && (
                                            <button 
                                                onClick={() => setSpoilerTab("book2")}
                                                style={{
                                                    flex: 1,
                                                    background: spoilerTab === "book2" ? "#991b1b" : "none",
                                                    border: "none",
                                                    color: spoilerTab === "book2" ? "white" : "#94a3b8",
                                                    padding: "6px 8px",
                                                    borderRadius: "6px",
                                                    fontSize: "11px",
                                                    fontWeight: 700,
                                                    cursor: "pointer",
                                                    transition: "all 0.15s"
                                                }}
                                            >
                                                Did you read book 2(Spoiler)
                                            </button>
                                        )}
                                    </div>

                                    {/* Description content panel */}
                                    <div style={{ fontSize: 14, color: "#cbd5e1", lineHeight: 1.7, marginBottom: 20 }}>
                                        {spoilerTab === "standard" && (
                                            selectedNode.contentIsSpoiler && !showSpoilers ? (
                                                <div style={{ padding: "16px", background: "rgba(251, 191, 36, 0.06)", border: "1px dashed rgba(251, 191, 36, 0.25)", borderRadius: "10px", textAlign: "center" }}>
                                                    <div style={{ fontSize: 28, marginBottom: 8 }}>🔒</div>
                                                    <p style={{ color: "#fbbf24", fontWeight: 700, fontSize: 13, margin: "0 0 4px" }}>Spoiler Content Hidden</p>
                                                    <p style={{ color: "#94a3b8", fontSize: 12, margin: 0 }}>Enable "Show Spoilers" at the top of the canvas to reveal this description.</p>
                                                </div>
                                            ) : (
                                                <div dangerouslySetInnerHTML={{ __html: selectedNode.content || "<p>No description provided.</p>" }} />
                                            )
                                        )}
                                        {spoilerTab === "book1" && (
                                            selectedNode.book1IsSpoiler && !showSpoilers ? (
                                                <div style={{ padding: "16px", background: "rgba(251, 191, 36, 0.06)", border: "1px dashed rgba(251, 191, 36, 0.25)", borderRadius: "10px", textAlign: "center" }}>
                                                    <div style={{ fontSize: 28, marginBottom: 8 }}>🔒</div>
                                                    <p style={{ color: "#fbbf24", fontWeight: 700, fontSize: 13, margin: "0 0 4px" }}>Spoiler Content Hidden</p>
                                                    <p style={{ color: "#94a3b8", fontSize: 12, margin: 0 }}>Enable "Show Spoilers" at the top of the canvas to reveal this description.</p>
                                                </div>
                                            ) : selectedNode.book1Content ? (
                                                <div dangerouslySetInnerHTML={{ __html: selectedNode.book1Content }} />
                                            ) : (
                                                <div style={{ padding: "16px", background: "rgba(239, 68, 68, 0.05)", border: "1px dashed rgba(239, 68, 68, 0.2)", borderRadius: "8px", textAlign: "center", color: "#ef4444", fontSize: "12px", fontWeight: 600 }}>
                                                    No Book 1 spoiler details recorded for this entry.
                                                </div>
                                            )
                                        )}
                                        {spoilerTab === "book2" && (
                                            selectedNode.book2IsSpoiler && !showSpoilers ? (
                                                <div style={{ padding: "16px", background: "rgba(251, 191, 36, 0.06)", border: "1px dashed rgba(251, 191, 36, 0.25)", borderRadius: "10px", textAlign: "center" }}>
                                                    <div style={{ fontSize: 28, marginBottom: 8 }}>🔒</div>
                                                    <p style={{ color: "#fbbf24", fontWeight: 700, fontSize: 13, margin: "0 0 4px" }}>Spoiler Content Hidden</p>
                                                    <p style={{ color: "#94a3b8", fontSize: 12, margin: 0 }}>Enable "Show Spoilers" at the top of the canvas to reveal this description.</p>
                                                </div>
                                            ) : selectedNode.book2Content ? (
                                                <div dangerouslySetInnerHTML={{ __html: selectedNode.book2Content }} />
                                            ) : (
                                                <div style={{ padding: "16px", background: "rgba(220, 38, 38, 0.05)", border: "1px dashed rgba(220, 38, 38, 0.2)", borderRadius: "8px", textAlign: "center", color: "#dc2626", fontSize: "12px", fontWeight: 600 }}>
                                                    No Book 2 spoiler details recorded for this entry.
                                                </div>
                                            )
                                        )}
                                    </div>

                                    {/* Connections / Relations links */}
                                    {selectedNode.connections && selectedNode.connections.length > 0 && (
                                        <div style={{ marginTop: 30, borderTop: "1px solid #1e293b", paddingTop: 20 }}>
                                            <h4 style={{ fontSize: 13, textTransform: "uppercase", color: "#888", letterSpacing: 0.5, marginBottom: 12 }}>Related Entities</h4>
                                            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                                {selectedNode.connections.map(connId => {
                                                    const target = loreNodes.find(n => n.id === connId);
                                                    if (!target) return null;
                                                    if (!showSpoilers && target.isSpoiler) return null;
                                                    const tInfo = LORE_TYPES[target.type] || LORE_TYPES.character;
                                                    return (
                                                        <div 
                                                            key={connId} 
                                                            onClick={() => jumpToNode(connId)}
                                                            style={{ 
                                                                display: "flex", 
                                                                alignItems: "center", 
                                                                gap: 8, 
                                                                padding: "8px 12px", 
                                                                background: "#1e293b66", 
                                                                borderRadius: 8, 
                                                                cursor: "pointer",
                                                                border: "1px solid transparent",
                                                                transition: "all 0.2s"
                                                            }}
                                                            onMouseEnter={e => e.currentTarget.style.borderColor = tInfo.color}
                                                            onMouseLeave={e => e.currentTarget.style.borderColor = "transparent"}
                                                        >
                                                            {target.imageUrl ? (
                                                                <img src={target.imageUrl} alt={target.title} style={{ width: 22, height: 22, borderRadius: "50%", objectFit: "cover" }} />
                                                            ) : (
                                                                <div style={{ width: 22, height: 22, borderRadius: "50%", background: `${tInfo.color}15`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                                    <FiBook size={10} style={{ color: tInfo.color }} />
                                                                </div>
                                                            )}
                                                            <span style={{ fontSize: 12, fontWeight: 600, color: "white" }}>{target.title}</span>
                                                            <span style={{ fontSize: 10, color: "#64748b", marginLeft: "auto" }}>{tInfo.label}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            ) : (
                /* Main Constellation Board (Sagas Tabletop) */
                <div style={{ flex: 1, padding: "40px 20px", display: "flex", flexDirection: "column", position: "relative" }}>
                    <div style={{ textAlign: "center", marginBottom: 40, zIndex: 1 }}>
                        <h1 style={{ fontSize: "2.8rem", fontWeight: 800, margin: "0 0 10px", background: "linear-gradient(135deg, #fff 0%, #c8a96e 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                            The Canvas
                        </h1>
                        <p style={{ color: "#94a3b8", fontSize: "1.1rem", maxWidth: "600px", margin: "0 auto" }}>
                            Explore the lore, maps, and character connections behind each saga. Select a saga book to unlock its canvas universe.
                        </p>
                    </div>

                    {loadingSagas ? (
                        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b" }}>
                            Loading saga map…
                        </div>
                    ) : sagas.length === 0 ? (
                        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", color: "#64748b" }}>
                            <FiBook size={48} style={{ marginBottom: 16, color: "#1a6b4a" }} />
                            <p style={{ fontSize: "1.1rem", margin: 0 }}>No Sagas are currently mapped to the Canvas.</p>
                            <p style={{ fontSize: "0.9rem", color: "#475569", marginTop: 4 }}>Check back soon for fantasy lore universes!</p>
                        </div>
                    ) : (
                        /* Sagas constellation workspace area */
                        <div style={{ 
                            position: "relative", 
                            flex: 1, 
                            minHeight: "500px", 
                            background: "radial-gradient(circle at center, #111827 0%, #070a10 100%)",
                            borderRadius: 24, 
                            border: "1px solid #1e293b",
                            boxShadow: "0 20px 50px rgba(0,0,0,0.6)"
                        }}>
                            {sagas.map(s => (
                                <div 
                                    key={s.id}
                                    onClick={() => setSelectedSaga(s)}
                                    style={{
                                        position: "absolute",
                                        left: `${s.x}%`,
                                        top: `${s.y}%`,
                                        transform: "translate(-50%, -50%)",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        zIndex: 10
                                    }}
                                >
                                    <div className="book-wrapper">
                                        <div 
                                            className={`book-cover-3d bind-${s.bookStyle || 'classic'}`}
                                            style={{
                                                background: s.imageUrl ? `url("${s.imageUrl}")` : (s.bookColor || "#8b1e0f"),
                                                backgroundSize: "cover",
                                                backgroundPosition: "center"
                                            }}
                                        >
                                            <div className="book-spine-effect" />
                                            <div className="book-pages-effect" />
                                        </div>
                                    </div>
                                    <h3 style={{ 
                                        margin: "12px 0 0", 
                                        fontSize: 14, 
                                        fontWeight: 700, 
                                        color: "#f3f4f6", 
                                        textAlign: "center",
                                        textShadow: "0 2px 4px rgba(0,0,0,0.8)",
                                        fontFamily: "'Playfair Display', serif"
                                    }}>
                                        {s.title}
                                    </h3>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
            {/* Zoomable Image Lightbox Modal */}
            {lightboxImage && (
                <div 
                    onClick={() => setLightboxImage(null)}
                    style={{
                        position: "fixed",
                        top: 0, left: 0, right: 0, bottom: 0,
                        background: "rgba(3, 7, 18, 0.95)",
                        zIndex: 2000,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "zoom-out",
                        backdropFilter: "blur(8px)"
                    }}
                >
                    {/* Close button */}
                    <button 
                        onClick={() => setLightboxImage(null)}
                        style={{
                            position: "absolute",
                            top: 24, right: 24,
                            background: "rgba(255, 255, 255, 0.1)",
                            border: "1px solid rgba(255, 255, 255, 0.15)",
                            borderRadius: "50%",
                            color: "white",
                            width: 44, height: 44,
                            cursor: "pointer", 
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "background 0.2s"
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)"}
                        onMouseLeave={e => e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)"}
                    >
                        <FiX size={24} />
                    </button>

                    {/* Image display with wheel zoom */}
                    <div 
                        style={{ 
                            position: "relative", 
                            overflow: "hidden", 
                            display: "flex", 
                            alignItems: "center", 
                            justifyContent: "center",
                            maxWidth: "85vw",
                            maxHeight: "80vh"
                        }}
                    >
                        <img 
                            src={lightboxImage} 
                            alt="Zoomed Details" 
                            onClick={e => e.stopPropagation()}
                            style={{
                                maxWidth: "100%",
                                maxHeight: "80vh",
                                objectFit: "contain",
                                borderRadius: 8,
                                transform: `scale(${lightboxZoom})`,
                                transition: "transform 0.12s ease-out",
                                cursor: "zoom-in",
                                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.75)"
                            }} 
                            onWheel={(e) => {
                                e.stopPropagation();
                                const delta = e.deltaY > 0 ? -0.1 : 0.1;
                                setLightboxZoom(prev => Math.min(4.0, Math.max(0.5, prev + delta)));
                            }}
                        />
                    </div>

                    {/* Lightbox Controls */}
                    <div 
                        onClick={e => e.stopPropagation()}
                        style={{
                            marginTop: 20,
                            display: "flex",
                            gap: 12,
                            alignItems: "center",
                            background: "rgba(15, 23, 42, 0.8)",
                            border: "1px solid rgba(255, 255, 255, 0.08)",
                            padding: "6px 16px",
                            borderRadius: 20,
                            boxShadow: "0 10px 25px rgba(0,0,0,0.5)"
                        }}
                    >
                        <button 
                            onClick={() => setLightboxZoom(prev => Math.max(0.5, prev - 0.2))}
                            style={{ background: "none", border: "none", color: "white", cursor: "pointer", fontSize: 16, fontWeight: 700 }}
                        >−</button>
                        <span style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", minWidth: 44, textAlign: "center" }}>
                            {Math.round(lightboxZoom * 100)}%
                        </span>
                        <button 
                            onClick={() => setLightboxZoom(prev => Math.min(4.0, prev + 0.2))}
                            style={{ background: "none", border: "none", color: "white", cursor: "pointer", fontSize: 16, fontWeight: 700 }}
                        >+</button>
                        <div style={{ height: 12, width: 1, background: "rgba(255,255,255,0.15)", margin: "0 4px" }} />
                        <button 
                            onClick={() => setLightboxZoom(1)}
                            style={{ background: "none", border: "none", color: "#6366f1", cursor: "pointer", fontSize: 11, fontWeight: 700, textTransform: "uppercase" }}
                        >Reset</button>
                    </div>
                    <span style={{ color: "#64748b", fontSize: 10, marginTop: 10, fontWeight: 500 }}>Use mouse scroll wheel or buttons to zoom and examine details</span>
                </div>
            )}
        </div>
    );
}
